/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useTranslation } from 'react-i18next';
import { PortalLane } from './PortalLane';
import { splitGroupsBySection } from '../types';
import type { CatalogPublication, PortalGroup } from '../types';
import { Title } from '@/shared/ui';

interface SectionProps {
  /** Which section this is; names its heading and its test id. */
  kind: 'social' | 'portals';
  groups: PortalGroup[];
  onSelect: (entry: CatalogPublication) => void;
}

/**
 * One section of the catalog (social networks or listing portals): a heading
 * above the lane of each portal that has publications. Renders nothing when
 * the section has none, so an agency that only uses portals does not see an
 * empty "Redes sociais" heading.
 *
 * @param props - The section kind, its portal groups and the card selection handler.
 * @returns The section element, or null when it has no publications.
 */
function PublicationSection({ kind, groups, onSelect }: SectionProps) {
  const { t } = useTranslation('@features/properties');
  if (groups.length === 0) return null;
  return (
    <section data-testid={`catalog-section-${kind}`} className="flex flex-col gap-(--space-5)">
      <Title order={2}
        className="m-0 text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]"
      >
        {t(`publications.catalog.sections.${kind}`)}
      </Title>
      <div className="flex flex-col gap-(--space-8)">
        {groups.map((group) => (
          <PortalLane key={group.portal} group={group} onSelect={onSelect} />
        ))}
      </div>
    </section>
  );
}

/**
 * The catalog's publications, split into a social-networks section and a
 * listing-portals section (OLX, ZAP, ...), each with one lane per portal.
 *
 * @param props - The portal groups and the card selection handler.
 * @returns The sections element.
 */
export function PublicationSections({ groups, onSelect }: {
  groups: PortalGroup[];
  onSelect: (entry: CatalogPublication) => void;
}) {
  const { social, portals } = splitGroupsBySection(groups);
  return (
    <div className="flex flex-col gap-(--space-10)">
      <PublicationSection kind="social" groups={social} onSelect={onSelect} />
      <PublicationSection kind="portals" groups={portals} onSelect={onSelect} />
    </div>
  );
}
